/* eslint-disable react-hooks/exhaustive-deps */
import { TextField } from "@material-ui/core";
import { FastField, getIn } from "formik";
import PropTypes from "prop-types";
import React, { memo, useEffect, useMemo, useState } from "react";
import NumberFormat from "react-number-format";
import RequiredLabel from "./RequiredLabel";
import configDefaultForm from "./configDefault";

const GlobitsVNDCurrencyInput = (props) => (
  <FastField {...props} name={props.name} shouldUpdate={shouldComponentUpdate}>
    {({ field, meta, form }) => (
      <Component
        {...props}
        field={field}
        meta={meta}
        setFieldValue={form.setFieldValue}
      />
    )}
  </FastField>
);

const NumericFormatCustom = React.forwardRef(function NumericFormatCustom(
  props,
  ref
) {
  const { onChange, isDot, suffix, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator={isDot ? "." : ","}
      decimalSeparator={isDot ? "," : "."}
      valueIsNumericString
      suffix={suffix}
    />
  );
});

NumericFormatCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  isDot: PropTypes.bool,
};

const Component = ({
  name,
  fullWidth = configDefaultForm.fullWidth,
  label,
  type = "text",
  debounceTime = 400, //default 0ms
  notDelay,
  field,
  meta,
  disabled,
  placeholder,
  isTextArea,
  minRowArea,
  required,
  requiredLabel,
  className = "",
  onChange,
  setFieldValue,
  readOnly,
  autoCapitalize = false, // tự động viết hoa
  isDot = false,
  suffix = "₫",
  colorText,
  maxValue,
  maxMessage = "",

  roundNumbers = 0,
}) => {
  const [value, setValue] = useState(field.value);
  const [t, setT] = useState(undefined);

  useEffect(() => {
    if (field.value !== value) setValue(field.value ?? "");
  }, [field.value]);

  const displayValue = useMemo(() => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;

    if (Number.isInteger(num)) return num.toString();

    // Làm tròn theo roundNumbers
    const rounded = num.toFixed(roundNumbers);

    // Nếu sau khi làm tròn không có dấu chấm (là số nguyên), trả về nguyên
    if (!rounded.includes(".")) return rounded;

    // Nếu có dấu chấm, loại bỏ số 0 thập phân thừa
    return rounded.replace(/\.?0+$/, "");
  }, [value, roundNumbers]);

  const handleChange = (e) => {
    let value = e.target.value;

    if (+maxValue && +value > +maxValue) {
      setValue(field.value ?? "");
      return;
    }
    if (autoCapitalize) {
      if (value.charAt(0) === value.charAt(0).toLowerCase()) {
        value = value.replace(
          value.charAt(0),
          value.charAt(0).toLocaleUpperCase()
        );
      }
      value = value.replace(/\s\S/g, (s) => s.toLocaleUpperCase());
    }
    setValue(value);
    if (!notDelay) {
      if (t) {
        clearTimeout(t);
      }
      if (onChange) {
        setT(setTimeout(() => onChange(e), debounceTime));
      } else {
        setT(
          setTimeout(
            () => setFieldValue(name, e.target.value ? e.target.value : null),
            debounceTime
          )
        );
      }
    } else {
      if (onChange) {
        onChange(e);
      } else {
        setFieldValue(name, e.target.value ? e.target.value : null);
      }
    }
  };
  const displayLabel = useMemo(
    () =>
      label ? (
        <RequiredLabel
          label={label}
          requiredLabel={required || requiredLabel}
        />
      ) : null,
    [label, required, requiredLabel]
  );

  return (
    <>
      {!fullWidth && displayLabel && (
        <label htmlFor={name}>{displayLabel}</label>
      )}
      <TextField
        multiline={isTextArea}
        id={name}
        name={name}
        value={displayValue ?? ""}
        fullWidth
        label={fullWidth && displayLabel ? displayLabel : null}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        type={type}
        error={Boolean(meta && meta.touched && meta.error)}
        helperText={meta && meta.touched && meta.error ? meta.error : ""}
        InputProps={{
          readOnly: readOnly,
          inputComponent: NumericFormatCustom,
          inputProps: {
            isDot: isDot,
          },
          style: {
            color: colorText,
          },
          endAdornment: suffix && (
            <span style={{ marginLeft: "8px", color: "#757575" }}>
              {suffix}
            </span>
          ),
        }}
        InputLabelProps={{
          htmlFor: name,
          shrink: true,
        }}
        className={`input-container ${className} ${
          readOnly ? "read-only" : ""
        } ${isTextArea ? "isTextArea" : ""}`}
        minRows={minRowArea}
      />
    </>
  );
};

const shouldComponentUpdate = (nextProps, currentProps) =>
  nextProps?.readOnly !== currentProps?.readOnly ||
  nextProps?.value !== currentProps?.value ||
  nextProps?.onChange !== currentProps?.onChange ||
  nextProps?.disabled !== currentProps?.disabled ||
  nextProps?.name !== currentProps?.name ||
  nextProps?.maxValue != currentProps?.maxValue ||
  Object.assign(nextProps).length !== Object.assign(currentProps).length ||
  getIn(nextProps.formik.values, currentProps.name) !==
    getIn(currentProps.formik.values, currentProps.name) ||
  getIn(nextProps.formik.errors, currentProps.name) !==
    getIn(currentProps.formik.errors, currentProps.name) ||
  getIn(nextProps.formik.touched, currentProps.name) !==
    getIn(currentProps.formik.touched, currentProps.name);

export default memo(GlobitsVNDCurrencyInput);
